import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoPipe } from '@jsverse/transloco';
import type {
  GridHeaderSelectionState,
  GridSelectionScope
} from '../../contracts/grid-row-selection.interface';
import { DataGridSelectionStore } from '../selection/data-grid-selection.store';
import {
  getGroupDisplayCount,
  getGroupHeaderColor,
  isGroupExpanded,
  resolveGroupDisplayKey,
  isLeafGroupLevel,
  resolveLeafColumns,
  rowsForSortState,
  type DataGridGroupBuildContext
} from '../data-grid-grouping.utils';
import {
  DATA_GRID_GROUP_HEADER_HEIGHT,
  groupHasMorePages,
  isGroupBucketLoading,
  shouldShowFilteredGroupEmptyState
} from '../data-grid-virtual-scroll.utils';
import type { DataGridColumn, DataGridGroupNode } from '../data-grid.types';
import {
  DataGridTableComponent,
  type DataGridColumnReorderEvent,
  type DataGridFilterMenuOpenEvent,
  type DataGridHeaderSortEvent
} from '../table/data-grid-table.component';
import type { DataGridGroupTreeViewModel } from './data-grid-group-tree.view-model';

/** Translation key and params for a group header's record count. */
export interface DataGridGroupCountLabel {
  /** Transloco key describing how complete the count is. */
  readonly key: string;
  /** Interpolation values for the key. */
  readonly params: Record<string, number>;
}

/**
 * Recursive group-header tree. Leaf nodes render {@link DataGridTableComponent}.
 */
@Component({
  selector: 'd-data-grid-group-tree',
  imports: [
    MatIconModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    TranslocoPipe,
    DataGridTableComponent
  ],
  templateUrl: './data-grid-group-tree.component.html',
  styleUrl: './data-grid-group-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--dg-group-header-height.px]': 'groupHeaderHeight'
  }
})
export class DataGridGroupTreeComponent {
  /** Group nodes at this level. */
  public groups: InputSignal<Array<DataGridGroupNode>> = input<Array<DataGridGroupNode>>(
    new Array<DataGridGroupNode>()
  );

  /** Ancestor key used for nested summary count lookup. */
  public parentKey: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /** Shared grouping chrome and leaf-table inputs, forwarded to nested trees. */
  public viewModel: InputSignal<DataGridGroupTreeViewModel> = input.required<DataGridGroupTreeViewModel>();

  /** Whether more primary group headers can be loaded. Only used at the root. */
  public hasMoreGroupHeaders: InputSignal<boolean> = input<boolean>(false);

  /** Whether this instance is the tree root (shows load-more headers). */
  public isRoot: InputSignal<boolean> = input<boolean>(false);

  /** Emits when a group header is toggled. */
  public toggleGroup: OutputEmitterRef<DataGridGroupNode> = output<DataGridGroupNode>();

  /** Emits when the user requests the next page of primary group headers. */
  public loadMoreGroupHeaders: OutputEmitterRef<void> = output<void>();

  /** Forwards leaf table header sort. */
  public headerSort: OutputEmitterRef<DataGridHeaderSortEvent> = output<DataGridHeaderSortEvent>();

  /** Forwards leaf table column reorder. */
  public columnReorder: OutputEmitterRef<DataGridColumnReorderEvent> = output<DataGridColumnReorderEvent>();

  /** Forwards leaf table filter-menu open. */
  public filterMenuOpen: OutputEmitterRef<DataGridFilterMenuOpenEvent> =
    output<DataGridFilterMenuOpenEvent>();

  /** Forwards row hover. */
  public rowHover: OutputEmitterRef<number | null> = output<number | null>();

  /** Forwards row action. */
  public rowAction: OutputEmitterRef<Record<string, unknown>> = output<Record<string, unknown>>();

  /** Forwards leaf load-more. */
  public loadMore: OutputEmitterRef<string | null> = output<string | null>();

  /** Forwards column width commits. */
  public columnWidthChange: OutputEmitterRef<{ field: string; width: number }> = output<{
    field: string;
    width: number;
  }>();

  /** Forwards resize-active so the grid host can set the resizing cursor. */
  public resizeActive: OutputEmitterRef<boolean> = output<boolean>();

  /** Per-grid selection store provided by `d-data-grid`. */
  private readonly selectionStore: DataGridSelectionStore = inject(DataGridSelectionStore);

  /** Summary context for header counts. */
  protected groupContext: Signal<DataGridGroupBuildContext> = computed((): DataGridGroupBuildContext => {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return {
      fieldToColumnMap: model.fieldToColumnMap,
      typeTotalCounts: model.typeTotalCounts,
      subGroupCounts: model.subGroupCounts,
      primaryGroupField: model.primaryGroupField,
      discriminatorField: model.discriminatorField,
      discriminatorLabels: model.discriminatorLabels
    };
  });

  /** Sticky group-header height used for nested thead offset. */
  protected readonly groupHeaderHeight: number = DATA_GRID_GROUP_HEADER_HEIGHT;

  /**
   * Whether a group is expanded.
   *
   * @param group - Group node.
   * @returns True when the body should render.
   */
  protected expanded(group: DataGridGroupNode): boolean {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return isGroupExpanded(group, model.collapsedGroups, model.expandedGroups, model.highCardinalityFields);
  }

  /**
   * Header color for a group.
   *
   * @param group - Group node.
   * @returns CSS color, or null.
   */
  protected headerColor(group: DataGridGroupNode): string | null {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return getGroupHeaderColor(
      group,
      model.discriminatorField,
      model.discriminatorColors,
      model.discriminatorLabels,
      model.fieldToColumnMap
    );
  }

  /**
   * Display count for a group header.
   *
   * @param group - Group node.
   * @returns Count to show.
   */
  protected displayCount(group: DataGridGroupNode): number {
    const summaryCount: number = getGroupDisplayCount(group, this.parentKey(), this.groupContext());
    if (!this.isLeafLevel(group)) {
      return summaryCount;
    }
    // A leaf's own bucket reports its total on the first page, whereas the group-summary query
    // that feeds `summaryCount` lands separately — until it does, the fallback is the loaded row
    // count, which reads as though the page size were the whole group. The bucket total already
    // defers to a summary count when one is known, so preferring it here only fills that gap.
    const bucketTotal: number = this.viewModel().groupBucketStates[group.path]?.totalCount ?? 0;
    return bucketTotal > 0 ? bucketTotal : summaryCount;
  }

  /**
   * Chooses how a group header states its record count.
   *
   * A leaf fetches continuation pages from its own scroll handler, so it rests at its first page
   * until the user scrolls it. Showing only the total there implies every row is present, so a
   * partially loaded leaf names both numbers.
   *
   * The count is a lower bound when the bucket still has pages but its total is not trustworthy.
   * That happens whenever the server reported no count for the leaf, because the total then falls
   * back to the loaded row count and the comparison is against itself — the header would read as
   * complete no matter how many rows were missing.
   *
   * Only leaf levels are treated this way. Parent groups aggregate their children's totals and
   * hold no rows, so their loaded count is always zero and the comparison is meaningless.
   *
   * @param group - Group node.
   * @returns Translation key and interpolation params for the header count.
   */
  protected countLabelFor(group: DataGridGroupNode): DataGridGroupCountLabel {
    const total: number = this.displayCount(group);
    if (!this.isLeafLevel(group)) {
      return { key: 'components.dataGrid.recordCount', params: { count: total } };
    }

    const loaded: number = group.rows.length;
    if (loaded < total) {
      return {
        key: 'components.dataGrid.partialRecordCount',
        params: { loaded, total }
      };
    }
    if (this.viewModel().groupBucketStates[group.path]?.hasNextPage === true) {
      return { key: 'components.dataGrid.atLeastRecordCount', params: { count: total } };
    }
    return { key: 'components.dataGrid.recordCount', params: { count: total } };
  }

  /**
   * Header label for a grouped field.
   *
   * @param field - Column field.
   * @returns Header, or the raw field.
   */
  protected headerFor(field: string): string {
    return this.viewModel().columnHeaders.get(field) ?? field;
  }

  /**
   * Display name for a discriminator slug.
   *
   * @param slug - Raw type slug.
   * @returns Display name, or the slug.
   */
  protected displayName(slug: string): string {
    return this.viewModel().discriminatorLabels[slug] ?? slug;
  }

  /**
   * Group-header label, preferring domain displayValue.
   *
   * @param group - Group node.
   * @returns Display label.
   */
  protected displayKey(group: DataGridGroupNode): string {
    return resolveGroupDisplayKey(group);
  }

  /**
   * Nested parent key passed to child trees.
   *
   * @param group - Current group.
   * @returns Child parentKey.
   */
  protected childParentKey(group: DataGridGroupNode): string {
    const parent: string | undefined = this.parentKey();
    return parent ? `${parent}:::${group.key}` : group.key;
  }

  /**
   * Sticky offset for a leaf table header stacked under ancestor group headers.
   *
   * @param group - Leaf group.
   * @returns Offset in pixels.
   */
  protected leafStickyTop(group: DataGridGroupNode): number {
    return (group.depth + 1) * this.groupHeaderHeight;
  }

  /**
   * Whether a leaf should show the filtered-empty message.
   *
   * @param group - Leaf group.
   * @returns True when filters excluded every row.
   */
  protected showFilteredEmpty(group: DataGridGroupNode): boolean {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return shouldShowFilteredGroupEmptyState(
      group.rows.length,
      group.totalCount,
      group.path,
      model.hasActiveColumnFilters,
      isGroupBucketLoading(group.path, model.groupBucketStates)
    );
  }

  /**
   * Whether a leaf bucket is loading more rows.
   *
   * @param path - Composite group path.
   * @returns True when loading.
   */
  protected bucketLoading(path: string): boolean {
    return isGroupBucketLoading(path, this.viewModel().groupBucketStates);
  }

  /**
   * Whether a fetch is currently in flight for this group's own bucket.
   *
   * Restricted to expanded leaf levels: parent groups aggregate their children and own no bucket,
   * and nothing is fetched for a collapsed group.
   *
   * This deliberately tracks live requests only. A "records still outstanding" signal was tried
   * instead and latched on permanently, because continuation pages are fetched from the leaf's
   * scroll handler — a group sits at its first page indefinitely until the user scrolls it, so the
   * gap between loaded rows and the total is a resting state rather than a transient one.
   *
   * @param group - Group node.
   * @returns True while a page request is outstanding for this group.
   */
  protected countPending(group: DataGridGroupNode): boolean {
    return this.isLeafLevel(group) && this.expanded(group) && this.bucketLoading(group.path);
  }

  /**
   * Whether this node is the last grouping level and should render a leaf table.
   *
   * @param group - Group node.
   * @returns True when a data table should render instead of nested headers.
   */
  protected isLeafLevel(group: DataGridGroupNode): boolean {
    return isLeafGroupLevel(group, this.viewModel().groupedFields);
  }

  /**
   * Columns for a leaf table.
   *
   * @param group - Leaf group.
   * @returns Visible columns.
   */
  protected columnsForLeaf(group: DataGridGroupNode): Array<DataGridColumn> {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return resolveLeafColumns(
      group,
      model.leafColumnsMap,
      model.hasDifferentiatorInChain,
      model.discriminatorField,
      model.discriminatorLabels
    );
  }

  /**
   * Sorted rows for a leaf, falling back to the group's loaded rows.
   *
   * @param group - Leaf group.
   * @returns Rows to render.
   */
  protected rowsForLeaf(group: DataGridGroupNode): Array<Record<string, unknown>> {
    const model: DataGridGroupTreeViewModel = this.viewModel();
    return rowsForSortState(
      group.rows,
      model.sortStates[group.path] ?? null,
      groupHasMorePages(group.path, group.rows.length, group.totalCount, model.groupBucketStates)
    );
  }

  /**
   * Whether the group-header checkbox is fully checked.
   *
   * @param group - Group node.
   * @returns True when the group is selected wholesale.
   */
  protected isGroupChecked(group: DataGridGroupNode): boolean {
    return this.groupHeaderState(group) === 'all';
  }

  /**
   * Whether the group-header checkbox is indeterminate.
   *
   * @param group - Group node.
   * @returns True when the group is partially selected.
   */
  protected isGroupIndeterminate(group: DataGridGroupNode): boolean {
    return this.groupHeaderState(group) === 'some';
  }

  /**
   * Toggles the group's membership in the current selection, including unfetched
   * rows. When every matching row is selected this deselects only this group.
   *
   * @param event - Click event; propagation is stopped so expand/collapse still works.
   * @param group - Group node.
   */
  protected onGroupCheckboxClick(event: MouseEvent, group: DataGridGroupNode): void {
    event.stopPropagation();
    const scope: GridSelectionScope = {
      path: group.path,
      field: group.field,
      estimatedCount: this.displayCount(group)
    };
    this.selectionStore.toggleScope(scope, group.rows);
  }

  /**
   * Header checkbox tri-state for a group.
   *
   * @param group - Group node.
   * @returns `none`, `some`, or `all`.
   */
  private groupHeaderState(group: DataGridGroupNode): GridHeaderSelectionState {
    if (!this.viewModel().rowSelectionEnabled) {
      return 'none';
    }
    return this.selectionStore.headerState(group.path, group.rows);
  }
}
